import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { server } from "../../server";
import useAuth from "../../useAuth";
import {
  FaChartBar,
  FaFileExcel,
  FaFilePdf,
  FaPrint,
  FaFilter,
  FaSearch,
  FaCalendarAlt,
  FaDownload,
  FaWallet,
  FaReceipt,
  FaExchangeAlt,
  FaCreditCard,
  FaMoneyBillWave,
  FaCheckCircle,
  FaExclamationCircle,
  FaLayerGroup,
  FaShieldAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import PrintableFeeReceipt from "./PrintableFeeReceipt";

export default function FeeReportsPage() {
  const [reportType, setReportType] = useState("collection");
  const [startDate, setStartDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      .toISOString()
      .split("T")[0],
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [selectedClass, setSelectedClass] = useState("ALL");
  const [selectedMode, setSelectedMode] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");

  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();

  // Print States
  const [printingReceipt, setPrintingReceipt] = useState(null);
  const printReceiptRef = useRef();
  const printReportRef = useRef();

  const handlePrintReceipt = useReactToPrint({
    contentRef: printReceiptRef,
    documentTitle: `Receipt_${printingReceipt?.receiptNumber || ""}`,
    pageStyle: `@page { size: A4 portrait; margin: 10mm; }`,
  });

  const handlePrintExecutiveReport = useReactToPrint({
    contentRef: printReportRef,
    documentTitle: `Financial_Fee_Report_${startDate}_to_${endDate}`,
    pageStyle: `@page { size: A4 landscape; margin: 10mm; }`,
  });

  const generateReport = async () => {
    try {
      setLoading(true);
      const url =
        reportType === "collection"
          ? `${server}/fee-reports/collection?startDate=${startDate}&endDate=${endDate}&paymentMode=${selectedMode}&class=${selectedClass}`
          : `${server}/fee-reports/defaulters?class=${selectedClass}`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.success) {
        setReportData(res.data.data || []);
        setSummary(res.data.summary || null);
      }
    } catch (error) {
      toast.error("Failed to generate financial report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) generateReport();
  }, [token, reportType, startDate, endDate, selectedMode, selectedClass]);

  const triggerPrintReceipt = (rc) => {
    setPrintingReceipt({ ...rc, isDuplicateCopy: true });
    setTimeout(() => {
      handlePrintReceipt();
    }, 150);
  };

  const handleExportCSV = () => {
    if (reportData.length === 0)
      return toast.error("No report data available to export!");

    let csvContent = "data:text/csv;charset=utf-8,";
    if (reportType === "collection") {
      csvContent +=
        "Receipt Number,Student Name,Admission No,Class,Payment Mode,Collection Date,Discount Applied,Fine Collected,Total Amount Paid\n";
      reportData.forEach((row) => {
        const dateStr = new Date(
          row.receiptDate || row.collectionDate || row.createdAt,
        ).toLocaleDateString();
        csvContent += `"${row.receiptNumber || ""}","${row.student?.studentName || "N/A"}","${row.student?.admissionNumber || "N/A"}","Class ${row.student?.class || "N/A"}","${row.paymentMode || "Cash"}","${dateStr}","${row.discountApplied || 0}","${row.fineCollected || 0}","${row.totalAmountPaid ?? row.amountPaid ?? 0}"\n`;
      });
    } else {
      csvContent +=
        "Student Name,Admission No,Class,Section,Due Date,Total Bill Amount,Paid Amount,Net Outstanding\n";
      reportData.forEach((row) => {
        const dateStr = new Date(row.dueDate).toLocaleDateString();
        const pending = (row.totalAmount || 0) - (row.paidAmount || 0);
        csvContent += `"${row.student?.studentName || "N/A"}","${row.student?.admissionNumber || "N/A"}","Class ${row.student?.class || "N/A"}","Section ${row.student?.section || "N/A"}","${dateStr}","${row.totalAmount || 0}","${row.paidAmount || 0}","${pending}"\n`;
      });
    }

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `Financial_Fee_Report_${new Date().toISOString().split("T")[0]}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredReportData = reportData.filter((row) => {
    if (!searchTerm.trim()) return true;
    const st = searchTerm.toLowerCase().trim();
    return (
      row.receiptNumber?.toLowerCase().includes(st) ||
      row.student?.studentName?.toLowerCase().includes(st) ||
      row.student?.admissionNumber?.toLowerCase().includes(st) ||
      row.paymentMode?.toLowerCase().includes(st)
    );
  });

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Compact Top Header Bar */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 rounded-xl shadow-xs">
            <FaChartBar size={18} />
          </div>
          <div>
            <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">
              Financial Fee Reports Engine
            </h1>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">
              Accountant audit registers, payment mode analytics, and class
              collection reports
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleExportCSV}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5"
          >
            <FaDownload size={11} /> Export CSV
          </button>
          <button
            onClick={handlePrintExecutiveReport}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5"
          >
            <FaPrint size={11} /> Print Audit Report
          </button>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="bg-white dark:bg-slate-900 p-3.5 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs mb-4 space-y-3">
        <div className="flex gap-2 border-b border-gray-200 dark:border-slate-800 pb-2">
          <button
            onClick={() => setReportType("collection")}
            className={`px-3 py-1.5 rounded-xl text-xs font-black transition flex items-center gap-1.5 ${
              reportType === "collection"
                ? "bg-emerald-600 text-white shadow-xs"
                : "bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300 hover:bg-gray-200"
            }`}
          >
            <FaWallet size={11} /> Collection Audit Register
          </button>
          <button
            onClick={() => setReportType("defaulters")}
            className={`px-3 py-1.5 rounded-xl text-xs font-black transition flex items-center gap-1.5 ${
              reportType === "defaulters"
                ? "bg-rose-600 text-white shadow-xs"
                : "bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300 hover:bg-gray-200"
            }`}
          >
            <FaExclamationCircle size={11} /> Defaulters Audit Register
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          {reportType === "collection" && (
            <>
              <div className="md:col-span-3">
                <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-900 dark:text-white outline-none"
                />
              </div>

              <div className="md:col-span-3">
                <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-900 dark:text-white outline-none"
                />
              </div>

              <div className="md:col-span-3">
                <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
                  Payment Mode
                </label>
                <select
                  value={selectedMode}
                  onChange={(e) => setSelectedMode(e.target.value)}
                  className="w-full px-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-900 dark:text-white outline-none"
                >
                  <option value="ALL">All Payment Modes</option>
                  <option value="Cash">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Debit Card">Debit Card</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                </select>
              </div>
            </>
          )}

          <div
            className={
              reportType === "collection" ? "md:col-span-3" : "md:col-span-4"
            }
          >
            <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
              Class Filter
            </label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="w-full px-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-900 dark:text-white outline-none"
            >
              <option value="ALL">All Classes</option>
              {[
                "1",
                "2",
                "3",
                "4",
                "5",
                "6",
                "7",
                "8",
                "9",
                "10",
                "11",
                "12",
              ].map((c) => (
                <option key={c} value={c}>
                  Class - {c}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-12 relative pt-1">
            <FaSearch className="absolute left-3 top-4 text-gray-400 dark:text-slate-500 text-xs" />
            <input
              type="text"
              placeholder="Search by Receipt No, Student Name, Reg No, Payment Mode..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-900 dark:text-white outline-none"
            />
          </div>
        </div>
      </div>

      {/* Financial Executive KPI Summary Cards */}
      {reportType === "collection" ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="bg-emerald-50 dark:bg-emerald-950/40 p-3 rounded-xl border border-emerald-200 dark:border-emerald-900 text-center shadow-xs">
            <span className="text-[9px] font-extrabold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
              Total Net Collection
            </span>
            <div className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-0.5">
              ₹{(summary?.totalCollection || 0).toLocaleString()}
            </div>
          </div>
          <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs">
            <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">
              Total Receipts Issued
            </span>
            <div className="text-xl font-black text-gray-900 dark:text-white mt-0.5">
              {summary?.totalReceipts || reportData.length}
            </div>
          </div>
          <div className="bg-indigo-50 dark:bg-indigo-950/40 p-3 rounded-xl border border-indigo-200 dark:border-indigo-900 text-center shadow-xs">
            <span className="text-[9px] font-extrabold uppercase tracking-wider text-indigo-700 dark:text-indigo-300">
              Average Receipt Value
            </span>
            <div className="text-xl font-black text-indigo-600 dark:text-indigo-400 mt-0.5">
              ₹{(summary?.avgReceipt || 0).toLocaleString()}
            </div>
          </div>
          <div className="bg-amber-50 dark:bg-amber-950/40 p-3 rounded-xl border border-amber-200 dark:border-amber-900 text-center shadow-xs">
            <span className="text-[9px] font-extrabold uppercase tracking-wider text-amber-700 dark:text-amber-300">
              Discounts & Fines
            </span>
            <div className="text-xs font-black text-amber-800 dark:text-amber-300 mt-1">
              Disc: ₹{(summary?.totalDiscount || 0).toLocaleString()} | Fine: ₹
              {(summary?.totalFine || 0).toLocaleString()}
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-rose-50 dark:bg-rose-950/40 p-3 rounded-xl border border-rose-200 dark:border-rose-900 text-center shadow-xs">
            <span className="text-[9px] font-extrabold uppercase tracking-wider text-rose-700 dark:text-rose-300">
              Total Defaulter Bills
            </span>
            <div className="text-xl font-black text-rose-600 dark:text-rose-400 mt-0.5">
              {summary?.totalDefaulters || reportData.length}
            </div>
          </div>
          <div className="bg-rose-100 dark:bg-rose-950 p-3 rounded-xl border border-rose-300 dark:border-rose-800 text-center shadow-xs">
            <span className="text-[9px] font-extrabold uppercase tracking-wider text-rose-800 dark:text-rose-200">
              Total Overdue Outstanding
            </span>
            <div className="text-xl font-black text-rose-700 dark:text-rose-300 mt-0.5">
              ₹{(summary?.totalDefaulterAmount || 0).toLocaleString()}
            </div>
          </div>
        </div>
      )}

      {/* Payment Mode & Class Breakdown Widgets */}
      {reportType === "collection" && summary?.modeSummary?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
          {/* Payment Mode Breakdown */}
          <div className="md:col-span-6 bg-white dark:bg-slate-900 p-4 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs">
            <h3 className="text-xs font-black text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <FaCreditCard className="text-emerald-500" /> Payment Mode Revenue
              Breakdown
            </h3>
            <div className="space-y-2.5">
              {summary.modeSummary.map((m, idx) => {
                const totalAll = summary.totalCollection || 1;
                const pct = Math.round((m.amount / totalAll) * 100);
                return (
                  <div key={idx} className="space-y-0.5 text-xs">
                    <div className="flex justify-between font-bold">
                      <span className="text-gray-800 dark:text-slate-200">
                        {m.mode} ({m.count} Receipts)
                      </span>
                      <span className="text-emerald-600 dark:text-emerald-400">
                        ₹{(m.amount || 0).toLocaleString()} ({pct}%)
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded-full"
                        style={{ width: `${pct}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Class Revenue Breakdown */}
          <div className="md:col-span-6 bg-white dark:bg-slate-900 p-4 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs">
            <h3 className="text-xs font-black text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <FaLayerGroup className="text-indigo-500" /> Class-Wise Collection
              Distribution
            </h3>
            <div className="space-y-2.5">
              {summary?.classSummary?.slice(0, 5).map((c, idx) => {
                const totalAll = summary.totalCollection || 1;
                const pct = Math.round((c.amount / totalAll) * 100);
                return (
                  <div key={idx} className="space-y-0.5 text-xs">
                    <div className="flex justify-between font-bold">
                      <span className="text-gray-800 dark:text-slate-200">
                        {c.class} ({c.count} Receipts)
                      </span>
                      <span className="text-indigo-600 dark:text-indigo-400">
                        ₹{(c.amount || 0).toLocaleString()} ({pct}%)
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-500 rounded-full"
                        style={{ width: `${pct}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Detailed Accountant Audit Register Table */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs overflow-hidden mb-8">
        <div className="p-3 bg-gray-50 dark:bg-slate-800/60 border-b border-gray-200 dark:border-slate-800 flex justify-between items-center text-xs font-black text-gray-700 dark:text-slate-300">
          <span>Itemized Audit Log ({filteredReportData.length} Records)</span>
        </div>

        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-800/80 border-b border-gray-200 dark:border-slate-800 text-gray-500 dark:text-slate-400 font-black text-[10px] uppercase">
                {reportType === "collection" ? (
                  <>
                    <th className="p-2.5">Receipt No</th>
                    <th className="p-2.5">Student Name</th>
                    <th className="p-2.5">Reg No</th>
                    <th className="p-2.5">Class</th>
                    <th className="p-2.5">Payment Mode</th>
                    <th className="p-2.5">Date</th>
                    <th className="p-2.5 text-right">Discount</th>
                    <th className="p-2.5 text-right">Fine</th>
                    <th className="p-2.5 text-right">Amount Paid</th>
                    <th className="p-2.5 text-center">Action</th>
                  </>
                ) : (
                  <>
                    <th className="p-2.5">Student Name</th>
                    <th className="p-2.5">Reg No</th>
                    <th className="p-2.5">Class (Sec)</th>
                    <th className="p-2.5">Due Date</th>
                    <th className="p-2.5 text-right">Total Bill</th>
                    <th className="p-2.5 text-right">Paid Amount</th>
                    <th className="p-2.5 text-right">Net Outstanding</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td
                    colSpan="10"
                    className="text-center py-12 text-emerald-600 font-bold"
                  >
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mx-auto mb-2"></div>
                    Generating detailed audit register...
                  </td>
                </tr>
              ) : filteredReportData.length === 0 ? (
                <tr>
                  <td
                    colSpan="10"
                    className="text-center py-12 text-gray-400 italic"
                  >
                    No matching report records found for the selected filter
                    criteria.
                  </td>
                </tr>
              ) : (
                filteredReportData.map((row, idx) => {
                  const amount = row.totalAmountPaid ?? row.amountPaid ?? 0;
                  return (
                    <tr
                      key={idx}
                      className="hover:bg-gray-50 dark:hover:bg-slate-800/40 transition"
                    >
                      {reportType === "collection" ? (
                        <>
                          <td className="p-2.5 font-black text-indigo-600 dark:text-indigo-400">
                            {row.receiptNumber}
                          </td>
                          <td className="p-2.5 font-black text-gray-900 dark:text-white">
                            {row.student?.studentName || "N/A"}
                          </td>
                          <td className="p-2.5 font-bold text-gray-500">
                            {row.student?.admissionNumber || "N/A"}
                          </td>
                          <td className="p-2.5 font-bold text-gray-800 dark:text-slate-200">
                            Class {row.student?.class}
                          </td>
                          <td className="p-2.5 font-semibold text-gray-700 dark:text-slate-300">
                            {row.paymentMode}
                          </td>
                          <td className="p-2.5 font-medium text-gray-500">
                            {new Date(
                              row.receiptDate ||
                                row.collectionDate ||
                                row.createdAt,
                            ).toLocaleDateString()}
                          </td>
                          <td className="p-2.5 text-right font-semibold text-amber-600">
                            ₹{(row.discountApplied || 0).toLocaleString()}
                          </td>
                          <td className="p-2.5 text-right font-semibold text-rose-600">
                            ₹{(row.fineCollected || 0).toLocaleString()}
                          </td>
                          <td className="p-2.5 text-right font-black text-emerald-600 dark:text-emerald-400">
                            ₹{amount.toLocaleString()}
                          </td>
                          <td className="p-2.5 text-center">
                            <button
                              onClick={() => triggerPrintReceipt(row)}
                              className="p-1.5 text-indigo-600 bg-indigo-50 dark:bg-indigo-950 dark:text-indigo-400 rounded-lg hover:bg-indigo-100 transition"
                              title="Re-Print Official Duplicate Receipt"
                            >
                              <FaPrint size={11} />
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="p-2.5 font-black text-gray-900 dark:text-white">
                            {row.student?.studentName || "N/A"}
                          </td>
                          <td className="p-2.5 font-bold text-gray-500">
                            {row.student?.admissionNumber || "N/A"}
                          </td>
                          <td className="p-2.5 font-bold text-gray-800 dark:text-slate-200">
                            Class {row.student?.class} ({row.student?.section})
                          </td>
                          <td className="p-2.5 font-medium text-gray-500">
                            {new Date(row.dueDate).toLocaleDateString()}
                          </td>
                          <td className="p-2.5 text-right font-black text-gray-900 dark:text-white">
                            ₹{(row.totalAmount || 0).toLocaleString()}
                          </td>
                          <td className="p-2.5 text-right font-black text-emerald-600 dark:text-emerald-400">
                            ₹{(row.paidAmount || 0).toLocaleString()}
                          </td>
                          <td className="p-2.5 text-right font-black text-rose-600 dark:text-rose-400">
                            ₹
                            {(
                              (row.totalAmount || 0) - (row.paidAmount || 0)
                            ).toLocaleString()}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Hidden Duplicate Receipt Printable Component */}
      <div className="hidden">
        <PrintableFeeReceipt ref={printReceiptRef} receipt={printingReceipt} />
      </div>

      {/* Hidden Executive Financial Audit Printable Report */}
      <div className="hidden">
        <div
          ref={printReportRef}
          className="p-8 bg-white text-gray-900 font-sans w-[297mm] min-h-[210mm]"
        >
          <div className="flex justify-between items-center border-b-2 border-emerald-600 pb-4 mb-6">
            <div>
              <h1 className="text-2xl font-black uppercase text-gray-900">
                Dee Novo Public School
              </h1>
              <p className="text-xs text-gray-600 font-semibold">
                Official Accountant Executive Fee Collection & Audit Report
              </p>
              <p className="text-xs text-gray-500">
                Period: {startDate} to {endDate} | Date Generated:{" "}
                {new Date().toLocaleDateString()}
              </p>
            </div>
            <div className="text-right">
              <div className="text-xl font-black text-emerald-600">
                FINANCIAL AUDIT REPORT
              </div>
              <p className="text-xs text-gray-500">Academic Year 2026-2027</p>
            </div>
          </div>

          {/* Report Summary Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="p-3 bg-gray-100 rounded border border-gray-300 text-center">
              <span className="text-[10px] font-bold text-gray-600 uppercase">
                Total Net Collection
              </span>
              <div className="text-lg font-black text-emerald-700">
                ₹{(summary?.totalCollection || 0).toLocaleString()}
              </div>
            </div>
            <div className="p-3 bg-gray-100 rounded border border-gray-300 text-center">
              <span className="text-[10px] font-bold text-gray-600 uppercase">
                Receipts Issued
              </span>
              <div className="text-lg font-black text-gray-900">
                {summary?.totalReceipts || reportData.length}
              </div>
            </div>
            <div className="p-3 bg-gray-100 rounded border border-gray-300 text-center">
              <span className="text-[10px] font-bold text-gray-600 uppercase">
                Average Receipt
              </span>
              <div className="text-lg font-black text-indigo-700">
                ₹{(summary?.avgReceipt || 0).toLocaleString()}
              </div>
            </div>
            <div className="p-3 bg-gray-100 rounded border border-gray-300 text-center">
              <span className="text-[10px] font-bold text-gray-600 uppercase">
                Total Discounts
              </span>
              <div className="text-lg font-black text-amber-700">
                ₹{(summary?.totalDiscount || 0).toLocaleString()}
              </div>
            </div>
          </div>

          <table className="w-full text-left border-collapse text-[10px] mb-6">
            <thead>
              <tr className="bg-gray-900 text-white uppercase font-bold">
                <th className="p-2">Receipt No</th>
                <th className="p-2">Student Name</th>
                <th className="p-2">Admission No</th>
                <th className="p-2">Class</th>
                <th className="p-2">Payment Mode</th>
                <th className="p-2">Date</th>
                <th className="p-2 text-right">Discount</th>
                <th className="p-2 text-right">Fine</th>
                <th className="p-2 text-right">Amount Paid</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-300">
              {filteredReportData.map((row, idx) => (
                <tr key={idx}>
                  <td className="p-2 font-bold text-indigo-700">
                    {row.receiptNumber}
                  </td>
                  <td className="p-2 font-bold">
                    {row.student?.studentName || "N/A"}
                  </td>
                  <td className="p-2">
                    {row.student?.admissionNumber || "N/A"}
                  </td>
                  <td className="p-2">Class {row.student?.class}</td>
                  <td className="p-2">{row.paymentMode}</td>
                  <td className="p-2">
                    {new Date(
                      row.receiptDate || row.collectionDate || row.createdAt,
                    ).toLocaleDateString()}
                  </td>
                  <td className="p-2 text-right">
                    ₹{(row.discountApplied || 0).toLocaleString()}
                  </td>
                  <td className="p-2 text-right">
                    ₹{(row.fineCollected || 0).toLocaleString()}
                  </td>
                  <td className="p-2 text-right font-black text-emerald-700">
                    ₹
                    {(
                      row.totalAmountPaid ??
                      row.amountPaid ??
                      0
                    ).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
